import { action, observable } from 'mobx'
import remotedev, { RemoteDevConfig } from 'mobx-remotedev'
import { inject, injectable } from 'inversify'
import {
  IArticle,
  IArticleBase,
  IArticlesStore,
  IErrorsStore,
  IFetchArticlesUseCase,
  IFetchArticleUseCase,
  IFetchFeaturedStoriesUseCase,
  IFetchMagazineArchivePageContentUseCase,
  IFetchMagazineSinglePageContentUseCase,
  IFetchMagazineTopPageContentUseCase,
  IFetchPickedUpFeaturesUseCase,
  IPublicArticle,
  IPublicArticleFactory,
  MagazineArchivePageContents,
  MagazineSinglePageContents,
  MagazineTopPageContents,
} from '@/types'
import symbols from '@/symbols'

const remoteDevConfig: RemoteDevConfig = {
  name: 'ArticlesStore',
  global: true,
  remote: false,
}

@remotedev(remoteDevConfig)
@injectable()
export default class ArticlesStore implements IArticlesStore {
  @observable storiesArticles: IArticle[] = []

  @observable publicArticles: Record<string, IPublicArticle> = {}

  @observable featuresArticles: IArticle[] = []

  @observable opinionsArticles: IArticle[] = []

  @observable newsArticles: IArticle[] = []

  @observable hasNextStoriesPage = true

  @observable hasNextFeaturesPage = true

  @observable hasNextOpinionsPage = true

  @observable hasNextNewsPage = true

  constructor(
    @inject(symbols.IErrorsStore) private errorsStore: IErrorsStore,
    @inject(symbols.IFetchArticlesUseCase) private fetchArticlesUseCase: IFetchArticlesUseCase,
    @inject(symbols.IFetchFeaturedStoriesUseCase) private fetchFeaturedStoriesUseCase: IFetchFeaturedStoriesUseCase,
    @inject(symbols.IFetchMagazineSinglePageContentUseCase)
    private fetchMagazineSinglePageContentUseCase: IFetchMagazineSinglePageContentUseCase,
    @inject(symbols.IFetchMagazineArchivePageContentUseCase)
    private fetchMagazineArchivePageContentUseCase: IFetchMagazineArchivePageContentUseCase,
    @inject(symbols.IFetchMagazineTopPageContentUseCase)
    private fetchMagazineTopPageContentUseCase: IFetchMagazineTopPageContentUseCase,
    @inject(symbols.IPublicArticleFactory) private publicArticleFactory: IPublicArticleFactory,
    @inject(symbols.IFetchPickedUpFeaturesUseCase) private fetchPickedUpFeaturesUseCase: IFetchPickedUpFeaturesUseCase,
    @inject(symbols.IFetchArticleUseCase) private fetchArticleUseCase: IFetchArticleUseCase
  ) {
    //
  }

  @action
  _addStoriesArticles(articles: IArticle[]): void {
    // 重複を除く
    const newArticles = articles.filter((newArticle) => {
      const isDuplicate = this.storiesArticles.some((a) => a.slug === newArticle.slug)
      return !isDuplicate
    })
    // 末尾に追加
    this.storiesArticles = this.storiesArticles.concat(newArticles)
  }

  @action
  _addFeaturesArticles(articles: IArticle[]): void {
    // 重複を除く
    const newArticles = articles.filter((newArticle) => {
      const isDuplicate = this.featuresArticles.some((a) => a.slug === newArticle.slug)
      return !isDuplicate
    })
    // 末尾に追加
    this.featuresArticles = this.featuresArticles.concat(newArticles)
  }

  @action
  _addOpinionsArticles(articles: IArticle[]): void {
    // 重複を除く
    const newArticles = articles.filter((newArticle) => {
      const isDuplicate = this.opinionsArticles.some((a) => a.slug === newArticle.slug)
      return !isDuplicate
    })
    // 末尾に追加
    this.opinionsArticles = this.opinionsArticles.concat(newArticles)
  }

  @action
  _addNewsArticles(articles: IArticle[]): void {
    // 重複を除く
    const newArticles = articles.filter((newArticle) => {
      const isDuplicate = this.newsArticles.some((a) => a.slug === newArticle.slug)
      return !isDuplicate
    })
    // 末尾に追加
    this.newsArticles = this.newsArticles.concat(newArticles)
  }

  @action
  _updateHasNextStoriesPage(hasNextPage: boolean): void {
    this.hasNextStoriesPage = hasNextPage
  }

  @action
  _updateHasNextFeaturesPage(hasNextPage: boolean): void {
    this.hasNextFeaturesPage = hasNextPage
  }

  @action
  _updateHasNextOpinionsPage(hasNextPage: boolean): void {
    this.hasNextOpinionsPage = hasNextPage
  }

  @action
  _updateHasNextNewsPage(hasNextPage: boolean): void {
    this.hasNextNewsPage = hasNextPage
  }

  @action
  updatePublicArticle(article: IArticleBase): void {
    if (this.publicArticles[article.slug]) {
      this.publicArticles[article.slug].update(article)
    } else {
      this.publicArticles[article.slug] = this.publicArticleFactory.create({ base: article })
    }
  }

  async fetchArticles(input: { category?: string; limit: number }): Promise<IArticle[]> {
    let categories: string[] = []
    switch (input.category) {
      case 'stories':
        categories = ['stories']
        break
      case 'features':
        categories = ['feature']
        break
      case 'opinions':
        categories = ['opinions']
        break
      case 'news':
        categories = ['news']
        break
      default:
        break
    }

    const output = await this.fetchArticlesUseCase.handle({
      categories,
      limit: input.limit,
    })
    if (output.error) {
      this.errorsStore.handle(output.error)

      return []
    }

    switch (input.category) {
      case 'stories':
        this._addStoriesArticles(output.articles)
        this._updateHasNextStoriesPage(output.hasNextPage)
        break
      case 'features':
        this._addFeaturesArticles(output.articles)
        this._updateHasNextFeaturesPage(output.hasNextPage)
        break
      case 'opinions':
        this._addOpinionsArticles(output.articles)
        this._updateHasNextOpinionsPage(output.hasNextPage)
        break
      case 'news':
        this._addNewsArticles(output.articles)
        this._updateHasNextNewsPage(output.hasNextPage)
        break
      default:
        break
    }

    return output.articles
  }

  async fetchTopPageContents(): Promise<MagazineTopPageContents> {
    const output = await this.fetchMagazineTopPageContentUseCase.handle()
    if (output.error) {
      this.errorsStore.handle(output.error)
    }

    return {
      pickups: output.pickups,
      stories: output.stories,
      opinions: output.opinions,
      features: output.features,
      news: output.news,
    }
  }

  async fetchCategoryArchivePageContents(category: string, limit: number): Promise<MagazineArchivePageContents> {
    let categories: string[] = []
    switch (category) {
      case 'stories':
        categories = ['stories']
        break
      case 'features':
        categories = ['feature']
        break
      case 'opinions':
        categories = ['opinions']
        break
      case 'news':
        categories = ['news']
        break
      default:
        break
    }

    const output = await this.fetchMagazineArchivePageContentUseCase.handle({ categories, limit })
    if (output.error) {
      this.errorsStore.handle(output.error)
    }

    return {
      articles: output.articles,
    }
  }

  async fetchSinglePageContents(slug: string): Promise<MagazineSinglePageContents> {
    const output = await this.fetchMagazineSinglePageContentUseCase.handle({ slug })
    if (output.error) {
      this.errorsStore.handle(output.error)
    }

    return {
      article: output.article,
      recommendedArticles: output.recommendedArticles,
    }
  }

  async fetchFeaturedStories(limit: number): Promise<IArticle[]> {
    const output = await this.fetchFeaturedStoriesUseCase.handle({
      limit,
    })
    if (output.error) {
      this.errorsStore.handle(output.error)

      return []
    }
    this._addStoriesArticles(output.featuredStories)

    return output.featuredStories
  }

  // 記事の Base を受け取って Instance にした上で Store で管理する
  // Store で管理するのはキャッシュの効果と不具合防止
  // Store で管理する場合、キャッシュが効いて一度取得した isLiked の値を保持できる
  // Store で管理しない場合、ブラウザバックで MobX の Observer が効かなくなる
  createArticleInstance(base: IArticleBase): IPublicArticle {
    if (!base) {
      return null
    }

    if (this.publicArticles[base?.slug]) {
      return this.publicArticles[base?.slug]
    }

    const instance = this.publicArticleFactory.create({ base })
    this.publicArticles[instance.slug] = instance

    return instance
  }

  async fetchPickedUpFeatures(limit: number): Promise<IArticle[]> {
    const output = await this.fetchPickedUpFeaturesUseCase.handle({
      limit,
    })
    if (output.error) {
      this.errorsStore.handle(output.error)

      return []
    }
    this._addFeaturesArticles(output.pickedUpFeatures)

    return output.pickedUpFeatures
  }

  async fetchArticle(slug: string): Promise<boolean> {
    const output = await this.fetchArticleUseCase.handle({ slug })

    if (output.article) {
      this.updatePublicArticle(output.article)
      return true
    }

    if (output.error) {
      this.errorsStore.handle(output.error)
    }

    return false
  }
}
